import { useSelector } from 'react-redux';
import { useMemo } from 'react';
import {
  selectDependenciesConfigurationItem,
  selectParsedConfigurationItems,
} from '../../../../reducers/documentation/documentationReducer';
import { selectEnvironments } from '../../../../reducers/global/globalReducer';
import {
  selectLayoutsByObjectName,
  selectRelatedObjectsByObjectName,
  selectParsedFields,
} from '../../../../reducers/global/parserReducer';
import { selectCrmOrgRollups } from '../../../../reducers/global/rollupReducers';
import {
  getConfigurationItemWithRTAttribution,
  createConfigurationItemFromRollupField,
  createConfigurationItemFromSweepField,
  createConfigurationItemFromAutomation,
  createConfigurationItemFromLayout,
  createConfigurationItemFromTag,
  createConfigurationItemFromField,
  createConfigurationItemFromParsedRule,
  createConfigurationItemFromRecordType,
} from '../../helpers';
import { RecordTypesLabelsByNameAndObjectName } from '../../types';
import { useDocumentation } from '../../useDocumentation';
import { selectTagsList } from '../../../../reducers/documentation/userInputsReducer';
import { selectAccountUsers } from '../../../../reducers/accountUsersReducer';
import { parseIdToValueAndNameToLabel } from '../annotations/tags/utils';
import { LayoutParserProperties, ParsedRecordType, ParsedRule } from '../../../parser/ParserTypes';
import { RollupForTable } from '../../../pages/rollups/rollupTypes';
import { ParsedFieldsByObject } from '../../../../reducers/global/globalReducerTypes';
import { UserInfo } from '../../../../types/UserInfoTypes';
import groupBy from 'lodash/groupBy';
import {
  getObjectNameFromRecordTypeId,
  getRecordTypeNameFromRecordTypeId,
} from '../../../Automations/helper';
import { ConfigurationItem } from '../DependenciesTypes';
import { selectChosenMetadataElement } from '../../../../reducers/documentation/metadataReducer';
import flatten from 'lodash/flatten';

export const useGetConfigurationItem = ({
  crmOrgId,
  recordTypeNamesUsedInCanvas,
}: {
  crmOrgId: string;
  recordTypeNamesUsedInCanvas: RecordTypesLabelsByNameAndObjectName;
}) => {
  const selectedDependency = useSelector(selectDependenciesConfigurationItem);
  const { parsedRecordTypes, parsedRules } = useDocumentation(crmOrgId);
  const { id, name, objectName } = selectedDependency ?? {};
  const objectLayouts = useSelector(selectLayoutsByObjectName(crmOrgId));
  const parsedConfigurationItems = useSelector(selectParsedConfigurationItems(crmOrgId));
  const parsedFields = useSelector(selectParsedFields(crmOrgId));
  const relatedObjects = useSelector(selectRelatedObjectsByObjectName(crmOrgId));
  const rollups = useSelector(selectCrmOrgRollups(crmOrgId));
  const globalEnvironments = useSelector(selectEnvironments);
  const tags = useSelector(selectTagsList);
  const users = useSelector(selectAccountUsers);
  const _parsedUsers = parseIdToValueAndNameToLabel(users ?? []);
  const chosenMetadataElements = useSelector(selectChosenMetadataElement);

  const { automations, alerts, matching, dedup, assignments, scheduledAssignments, sweepFields } =
    globalEnvironments?.[crmOrgId]?.data ?? {};

  const indexedLookups = useMemo(() => {
    const tagsById = new Map(tags?.map((tag) => [tag.id, tag]) ?? []);
    const usersById = new Map(_parsedUsers?.map((user) => [user.value, user]) ?? []);
    const layoutsById = new Map<string, LayoutParserProperties>();
    const layoutsByName = new Map<string, LayoutParserProperties>();
    const allLayoutsArray =
      objectLayouts?.[objectName ?? '']?.layouts ??
      Object.values(objectLayouts ?? {})
        .map((layouts) => layouts.layouts)
        .flat();

    allLayoutsArray?.forEach((layout) => {
      if (layout.id) layoutsById.set(layout.id, layout);
      if (layout.name) layoutsByName.set(layout.name, layout);
    });

    const relatedObjectsById = new Map<string, ConfigurationItem>();

    Object.entries(relatedObjects ?? {}).forEach(([, relatedObjectData]) => {
      const parsedFields = flatten(Object.values(relatedObjectData.parsedFields ?? {}));
      parsedFields.forEach((field) => {
        if (field.id) relatedObjectsById.set(field.id, createConfigurationItemFromField(field));
      });

      const parsedRules = relatedObjectData.parsedRules;
      if (parsedRules) {
        parsedRules.forEach((rule) => {
          if (rule.id) relatedObjectsById.set(rule.id, createConfigurationItemFromParsedRule(rule));
        });
      }

      const parsedRecordTypes = relatedObjectData.parsedRecordTypes;
      if (parsedRecordTypes) {
        parsedRecordTypes.forEach((recordType) => {
          if (recordType.id)
            relatedObjectsById.set(
              recordType.id,
              createConfigurationItemFromRecordType(recordType) as ConfigurationItem,
            );
        });
      }
    });

    const rulesById = new Map<string, ParsedRule>();
    const rulesByName = new Map<string, ParsedRule[]>();
    parsedRules?.forEach((rule) => {
      if (rule.id) rulesById.set(rule.id, rule);
      if (rule.name) {
        const existingRules = rulesByName.get(rule.name) ?? [];
        rulesByName.set(rule.name, [...existingRules, rule]);
      }
    });

    const rollupsById = new Map<string, RollupForTable>();
    const rollupsByFieldId = new Map<string, RollupForTable>();
    rollups?.forEach((rollup) => {
      if (rollup.rollupId) rollupsById.set(rollup.rollupId, rollup);
      if (rollup.rollupField?.id) rollupsByFieldId.set(rollup.rollupField.id, rollup);
    });

    const recordTypesByName = new Map<string, ParsedRecordType>();
    const recordTypesByKey = new Map<string, ParsedRecordType>();
    parsedRecordTypes?.forEach((rt) => {
      if (rt.name) recordTypesByName.set(`${rt.name}-${rt.objectApiName}`, rt);
      const rtName = getRecordTypeNameFromRecordTypeId(rt.id || '');
      const rtObjectName = getObjectNameFromRecordTypeId(rt.id || '');
      if (rtName && rtObjectName) {
        recordTypesByKey.set(`${rtName}-${rtObjectName}`, rt);
      }
    });

    const sweepFieldsById = new Map(Object.entries(sweepFields ?? {}));

    return {
      tagsById,
      usersById,
      layoutsById,
      layoutsByName,
      rulesById,
      rulesByName,
      rollupsById,
      rollupsByFieldId,
      recordTypesByName,
      recordTypesByKey,
      sweepFieldsById,
      allLayoutsArray,
      relatedObjectsById,
    };
  }, [
    tags,
    _parsedUsers,
    objectLayouts,
    objectName,
    parsedRules,
    rollups,
    parsedRecordTypes,
    sweepFields,
    relatedObjects,
  ]);

  const cachedExpensiveOperations = useMemo(() => {
    const makeNotUndefined = (items?: AutomationStructureNew[]) => items ?? [];

    const mergedAutomations = groupBy(
      [
        ...makeNotUndefined(automations),
        ...makeNotUndefined(alerts),
        ...makeNotUndefined(assignments),
        ...makeNotUndefined(matching),
        ...makeNotUndefined(dedup),
        ...makeNotUndefined(scheduledAssignments),
      ].flat(),
      'automationId',
    );

    const configItemsById = new Map<string, ConfigurationItem>();
    const configItemsByName = new Map<string, ConfigurationItem[]>();
    parsedConfigurationItems?.forEach((item) => {
      if (item.id) configItemsById.set(item.id, item);
      if (item.name) {
        const existingItems = configItemsByName.get(item.name) ?? [];
        configItemsByName.set(item.name, [...existingItems, item]);
      }
    });

    const allFieldsFlattened: Array<any> = [];
    const fieldsById = new Map<string, any>();

    if (parsedFields) {
      Object.keys(parsedFields).forEach((objectName) => {
        parsedFields[objectName]?.forEach((field) => {
          const fieldWithObject = { ...field, objectName };
          allFieldsFlattened.push(fieldWithObject);
          if (field.id) fieldsById.set(field.id, fieldWithObject);
        });
      });
    }

    const metadataElementsById = new Map(chosenMetadataElements?.map((el) => [el.id, el]) ?? []);

    return {
      mergedAutomations,
      configItemsById,
      configItemsByName,
      allFieldsFlattened,
      fieldsById,
      metadataElementsById,
    };
  }, [
    automations,
    alerts,
    assignments,
    matching,
    dedup,
    scheduledAssignments,
    parsedConfigurationItems,
    parsedFields,
    chosenMetadataElements,
  ]);

  const searchCriteria = useMemo(
    () => ({
      id,
      name,
      objectName,
      searchByName: !id,
    }),
    [id, name, objectName],
  );

  const item = useMemo(() => {
    if (!selectedDependency) return undefined;

    const _item = getOptimizedConfigurationItem({
      searchCriteria,
      recordTypeNamesUsedInCanvas,
      parsedFields,
      users,
      indexedLookups,
      cachedExpensiveOperations,
    });

    // Add parent type information
    const withParentType = _item
      ? {
          ..._item,
          parentType: _item?.parentType ?? selectedDependency?.parentType ?? '',
          dependencyType: _item?.dependencyType ?? selectedDependency?.dependencyType ?? '',
        }
      : undefined;

    return withParentType;
  }, [
    selectedDependency,
    searchCriteria,
    recordTypeNamesUsedInCanvas,
    parsedFields,
    users,
    indexedLookups,
    cachedExpensiveOperations,
  ]);

  return item;
};

export const getOptimizedConfigurationItem = ({
  searchCriteria,
  recordTypeNamesUsedInCanvas,
  parsedFields,
  users,
  indexedLookups,
  cachedExpensiveOperations,
}: {
  searchCriteria: { id?: string; name?: string; objectName?: string; searchByName: boolean };
  recordTypeNamesUsedInCanvas: RecordTypesLabelsByNameAndObjectName;
  parsedFields?: ParsedFieldsByObject;
  users?: UserInfo[];
  indexedLookups: any;
  cachedExpensiveOperations: any;
}): ConfigurationItem | undefined => {
  const { id, name, objectName, searchByName } = searchCriteria;

  if (!id && !name) {
    return undefined;
  }

  const _id = id ?? '';
  const _name = name ?? '';
  const _objectName = objectName ?? '';

  const tag = indexedLookups.tagsById.get(_id);
  const user = indexedLookups.usersById.get(_id);

  if (tag || user) {
    return createConfigurationItemFromTag(tag ?? { ...user, id: user?.value });
  }

  const layout = searchByName
    ? indexedLookups.layoutsByName.get(_name)
    : indexedLookups.layoutsById.get(_id);

  if (layout) {
    return createConfigurationItemFromLayout(layout, _objectName);
  }

  const rule = searchByName
    ? indexedLookups.rulesByName.get(_name)?.[0] // Take first match for name-based search
    : indexedLookups.rulesById.get(_id);

  if (rule) {
    const ruleWithAttribution = getConfigurationItemWithRTAttribution(
      rule,
      recordTypeNamesUsedInCanvas,
    );
    return ruleWithAttribution;
  }

  const relatedObject = indexedLookups.relatedObjectsById.get(_id);
  if (relatedObject) {
    return relatedObject;
  }

  const rollup = indexedLookups.rollupsById.get(_id) || indexedLookups.rollupsByFieldId.get(_id);

  if (rollup) {
    const parsedRollupObjectFields = parsedFields?.[rollup?.objectName ?? ''];
    const { usage, link } = parsedRollupObjectFields?.find((field) => field.name === _name) ?? {};

    return createConfigurationItemFromRollupField({
      id: rollup.rollupId,
      sweepField: rollup?.rollupField,
      usage,
      link,
    });
  }

  const field = cachedExpensiveOperations.fieldsById.get(_id);

  if (field) {
    return createConfigurationItemFromField(field);
  }

  const configItem = searchByName
    ? cachedExpensiveOperations.configItemsByName.get(_name)?.[0]
    : cachedExpensiveOperations.configItemsById.get(_id);

  if (configItem) {
    return configItem;
  }

  const recordTypeKey = `${_name}-${_objectName}`;
  let parsedRecordType = indexedLookups.recordTypesByName.get(recordTypeKey);

  if (!parsedRecordType) {
    const rtName = getRecordTypeNameFromRecordTypeId(_id);
    const rtObjectName = getObjectNameFromRecordTypeId(_id);
    if (rtName && rtObjectName) {
      parsedRecordType = indexedLookups.recordTypesByKey.get(`${rtName}-${rtObjectName}`);
    }
  }

  if (parsedRecordType) {
    return createConfigurationItemFromRecordType(parsedRecordType);
  }

  const sweepField = indexedLookups.sweepFieldsById.get(_id);

  if (sweepField) {
    return createConfigurationItemFromSweepField(sweepField);
  }

  const metadataElement = cachedExpensiveOperations.metadataElementsById.get(_id);

  if (metadataElement) {
    return metadataElement;
  }

  const automation = cachedExpensiveOperations.mergedAutomations[_id]?.[0];
  return automation ? createConfigurationItemFromAutomation(automation, users) : undefined;
};
